import React, { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import TrustpilotBar from '@/components/TrustpilotBar';
import { TrustpilotReview, SubjectType } from '@/components/WukongReviews/data';
import { getReviewsForSubject } from '@/components/WukongReviews/utils';

import 'swiper/css';

// 国家名 → 本地国旗图片路径映射（emoji 风格，跨平台兼容）
const COUNTRY_FLAG_URL: Record<string, string> = {
  Australia: '/images/wukong-reviews/Australia.png',
  'New Zealand': '/images/wukong-reviews/newZealand.png',
  'United States': '/images/wukong-reviews/unitedStates.png',
  Canada: '/images/wukong-reviews/canada.png',
  Singapore: '/images/wukong-reviews/singapore.png',
  Thailand: '/images/wukong-reviews/thailand.png',
  Germany: '/images/wukong-reviews/germany.png',
  'United Kingdom': '/images/wukong-reviews/unitedKindom.png',
  Belgium: '/images/wukong-reviews/Belgium.png',
};

/**
 * Trustpilot 绿色星星图标
 */
const TrustpilotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className || 'size-5 lg:size-6'}>
    <rect width="24" height="24" rx="4" fill="#00B67A" />
    <path
      d="M12 4L13.8 9.2L19.2 9.6L15.2 13L16.4 18.2L12 15.4L7.6 18.2L8.8 13L4.8 9.6L10.2 9.2L12 4Z"
      fill="white"
    />
  </svg>
);

/**
 * 星级评分组件
 */
const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex items-center space-x-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        viewBox="0 0 24 24"
        fill={i < rating ? '#FFB800' : '#E5E5E5'}
        className="size-4">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
      </svg>
    ))}
  </div>
);

/**
 * 好评卡片组件
 */
export const ReviewCard: React.FC<{
  review: TrustpilotReview;
  className?: string;
}> = ({ review, className = '' }) => {
  const flagUrl = COUNTRY_FLAG_URL[review.region];

  return (
    <a
      href={review.link}
      target="_blank"
      rel="noopener noreferrer"
      className={`block cursor-pointer rounded-xl border border-border-deep bg-bg-card_white bg-clip-padding p-5 transition-shadow hover:shadow-md lg:rounded-xl lg:p-5 xl:rounded-2xl xl:p-6 ${className}`}>
      <div className="flex h-full flex-col space-y-4">
        {/* 头部: 姓名、地区、Trustpilot 图标 */}
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center space-x-2">
            <span className="truncate text-[14px] font-bold text-text-primary">
              {review.name}
            </span>
            <span className="flex shrink-0 items-center whitespace-nowrap text-[14px] text-text-secondary_3">
              {review.region}
              {flagUrl && (
                <img
                  src={flagUrl}
                  alt=""
                  className="ml-1 inline-block h-4 w-4"
                />
              )}
            </span>
          </div>
          <TrustpilotIcon className="size-5 shrink-0 xl:size-6" />
        </div>

        {/* 分隔线 */}
        <div className="h-px w-full bg-border-deep" />

        {/* 日期和星级 */}
        <div className="flex items-center space-x-3">
          <span className="text-[14px] text-text-secondary_3">
            {review.date}
          </span>
          <StarRating rating={review.rating} />
        </div>

        {/* 评论内容 */}
        <p className="line-clamp-3 text-[16px] font-medium leading-[1.5] text-text-primary">
          {review.comment}
        </p>
      </div>
    </a>
  );
};

/**
 * 好评卡片网格组件
 * 包含 Trustpilot 评分条 + PC 3列 grid + 移动端 Swiper
 *
 * @param subject - 科目类型，用于筛选好评
 * @param count - 显示数量，默认 3
 */
interface ReviewCardGridProps {
  subject: SubjectType;
  count?: number;
}

const ReviewCardGrid: React.FC<ReviewCardGridProps> = ({
  subject,
  count = 3,
}) => {
  const reviews = useMemo(
    () => getReviewsForSubject(subject, count),
    [subject, count],
  );

  return (
    <>
      {/* Trustpilot 评分条 */}
      <div className="mt-6 flex justify-center lg:mt-10">
        <TrustpilotBar />
      </div>

      {/* PC 端 - 3 列好评卡片 */}
      <div className="container mt-8 hidden lg:block xl:mt-10">
        <div className="grid grid-cols-3 gap-4 xl:gap-6">
          {reviews.map(review => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      </div>

      {/* 移动端 - Swiper 横向滚动 */}
      <div className="mt-6 overflow-hidden lg:hidden">
        <div className="pl-4">
          <Swiper
            slidesPerView="auto"
            spaceBetween={12}
            className="!overflow-visible !pr-4">
            {reviews.map(review => (
              <SwiperSlide key={review.id} className="!h-auto !w-[300px]">
                <ReviewCard review={review} className="h-full" />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </>
  );
};

export default ReviewCardGrid;
